<template>
  <div class="clg-menu">
    <!-- 供应链标题 -->
    <div v-if="supplyChainInfo.title" class="supplyChainBox" :class="{ collapsed }" @click.stop="goHome(true)">
      <img v-if="supplyChainInfo.logo" :src="supplyChainInfo.logo" alt="供应链Logo" class="supplyLogo" />
      <span v-show="!collapsed" class="supplyTitle">{{ supplyChainInfo.title }}</span>
    </div>
    <!-- 默认 Logo -->
    <div v-else v-show="!collapsed" class="logoBox" @click="goHome(false)" />

    <a-menu
      id="member-menu"
      v-model="selectedKeys"
      :open-keys.sync="openKeys"
      class="member-menu"
      mode="inline"
      :inline-collapsed="collapsed"
      :class="{ isCollapsed: collapsed }"
    >
      <template v-for="item in menusOption">
        <!-- 无子菜单项 -->
        <a-menu-item v-if="!hasChildren(item)" :key="item.key" :title="$t(item.text)" @click="toMenuItem(item)">
          <menu-icon :item="item" />
          <span>{{ $t(item.text) }}</span>
          <badge-count v-if="item.text === '消息中心'" :count="allUnReadNum" />
        </a-menu-item>

        <!-- 有子菜单项 -->
        <a-sub-menu v-else :key="`${item.key}-sub`">
          <template #title>
            <span>
              <menu-icon :item="item" />
              <span class="menu-name">{{ $t(item.text) }}</span>
              <badge-count v-if="item.text === '云上展览系统'" :count="auditingNum" />
            </span>
          </template>

          <template v-for="subItem in item.children">
            <!-- 二级菜单 - 无子项 -->
            <a-menu-item
              v-if="!hasChildren(subItem)"
              :key="subItem.key"
              :title="$t(subItem.text)"
              @click="toMenuItem(subItem)"
            >
              <span>{{ $t(subItem.text) }}</span>
            </a-menu-item>

            <!-- 二级菜单 - 有子项 -->
            <a-sub-menu v-else :key="`${subItem.key}-sub`" class="sub-menu-level-2">
              <template #title>
                <span @click="toMenuItem(subItem)">
                  <span>{{ $t(subItem.text) }}</span>
                </span>
              </template>

              <a-menu-item
                v-for="subTwoItem in subItem.children"
                :key="subTwoItem.key"
                :title="$t(subTwoItem.text)"
                :disabled="subTwoItem.specialState === 'disable'"
                @click="toMenuItem(subTwoItem)"
              >
                <span>{{ $t(subTwoItem.text) }}</span>
              </a-menu-item>
            </a-sub-menu>
          </template>
        </a-sub-menu>
      </template>
    </a-menu>

    <a-space class="messageBox" @click.stop="goNotifications">
      <a-icon type="message" />
      <template v-if="!collapsed">
        <span>消息 </span>
        <a-badge :count="allUnReadNum" :offset="[0, 5]" dot />
      </template>
    </a-space>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { Role, System } from '@/const/constConfig'
import { contractors, designer, exhibitor, organizer, visitor } from '@/const/menu'

// 角色菜单映射（静态，避免重复创建）
const ROLE_MENU_MAP = {
  1: visitor,
  2: organizer,
  3: exhibitor,
  4: contractors,
  5: designer,
}

// 需要从 href 中去除的角色路径前缀
const ROLE_PREFIXES = ['/visitor', '/organizer', '/exhibitor', '/contractors', '/designer']

// 去除 href 中的角色前缀（如 /exhibitor/foo -> /foo）
const stripRolePrefix = (href) => {
  if (!href || typeof href !== 'string') return href
  const matched = ROLE_PREFIXES.find((p) => href === p || href.startsWith(`${p}/`))
  if (!matched) return href
  return href.slice(matched.length) || '/'
}

// 需要过滤的菜单项配置
const FILTER_CONFIG = {
  employeeManagement: {
    客户管理系统: ['企业数据'],
    财务中心: ['发票管理'],
    企业云展系统: ['我的云企业', '我的企业动态', '企业漫游'],
  },
  english: {
    招商招标系统: ['招商招标系统'],
    财务中心: ['我的钱包'],
    获客工具: ['云招聘'],
    excludeMenus: ['我的会议', '我的云设计'],
  },
}

// 函数式子组件 - 菜单图标
const MenuIcon = {
  functional: true,
  props: ['item'],
  render(h, { props }) {
    const { item } = props
    if (item.icon) {
      return h('a-icon', { props: { type: item.icon } })
    }
    if (item.classIcon) {
      return h('span', { class: 'anticon' }, [h('i', { class: ['icon', 'iconfont', item.classIcon] })])
    }
    return null
  },
}

// 函数式子组件 - 徽章计数
const BadgeCount = {
  functional: true,
  props: ['count'],
  render(h, { props }) {
    if (!props.count) return null
    return h('span', { class: 'talkUnReadNum' }, props.count)
  },
}

export default {
  name: 'ConsoleMenu',

  components: {
    MenuIcon,
    BadgeCount,
  },

  props: {
    collapsed: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedKeys: [],
      openKeys: [],
      cachedOpenKeys: [],
    }
  },

  computed: {
    ...mapState({
      accountLevel: (state) => state.userInfo.accountLevel,
      auditingNum: (state) => state.auditingNum,
      supplyChainInfo: (state) => state.supplyChainInfo,
      locale: (state) => state.locale,
    }),

    ...mapGetters({
      companyFlag: 'companyFlag',
      roleId: 'roleId',
      userInfo: 'userInfo',
      memunicode: 'memunicode',
      consolePath: 'getConsolePath',
      allUnReadNum: 'chat/allUnReadNum',
    }),

    // 套餐码
    setMealCode() {
      return this.userInfo?.memberSetMealInfo?.setMealCode || 0
    },

    // 是否显示员工管理(旗舰版 || 云宇宙版的企业用户)
    showEmployeeManagement() {
      const isParent = this.accountLevel !== Role.AccountLevel.Child
      const isTopVip = this.setMealCode === 70000 || this.setMealCode === 80000
      return isParent && isTopVip
    },

    // 是否英文环境
    isEnglish() {
      return this.locale === 'en'
    },

    // 是否个人身份
    isPerson() {
      return this.companyFlag === System.Identity.Person
    },

    // 是否基础版用户
    isBaseUser() {
      return this.userInfo?.memberSetMealInfo?.isBase === 1 || this.accountLevel === Role.AccountLevel.User
    },

    /**
     * 获取并过滤菜单数据
     */
    menusOption() {
      const rawData = ROLE_MENU_MAP[this.roleId]
      if (!rawData) return []

      // 深拷贝菜单数据
      let menuData = JSON.parse(JSON.stringify(rawData))

      // 应用过滤规则
      menuData = this.applyFilters(menuData)

      // 去除 href 中的角色前缀，与当前实际路由 path 对齐
      menuData = this.normalizeHref(menuData)

      return menuData
    },
  },

  watch: {
    $route: {
      handler: 'updateMenuState',
      immediate: true,
    },

    collapsed(isCollapsed) {
      if (isCollapsed) {
        this.cachedOpenKeys = [...this.openKeys]
        this.openKeys = []
      } else {
        this.openKeys = this.cachedOpenKeys
      }
    },

    // 菜单数据变化时更新状态
    menusOption() {
      this.$nextTick(() => this.updateMenuState(this.$route))
    },
  },

  async mounted() {
    await this.$store.dispatch('getUserInfo')

    if (this.roleId > 1) {
      this.$store.dispatch('getExhibitionAuditNum')
    }

    this.updateMenuState(this.$route)
  },

  methods: {
    /**
     * 判断菜单项是否有子菜单
     */
    hasChildren(item) {
      return item.children?.length > 0
    },

    /**
     * 递归去除 href 中的角色前缀
     */
    normalizeHref(list) {
      return list.map((item) => {
        const next = { ...item, href: stripRolePrefix(item.href) }
        if (this.hasChildren(item)) {
          next.children = this.normalizeHref(item.children)
        }
        return next
      })
    },

    /**
     * 应用所有过滤规则
     */
    applyFilters(menuData) {
      let result = menuData

      // 1. 员工管理权限过滤
      result = this.filterByEmployeeManagement(result)

      // 2. 套餐过滤
      result = this.filterBySetMeal(result)

      // 3. 身份过滤
      result = this.filterByIdentity(result)

      // 4. 基础版过滤
      result = this.filterByBaseVersion(result)

      // 5. 语言环境过滤
      result = this.filterByLocale(result)

      return result
    },

    /**
     * 员工管理权限过滤
     */
    filterByEmployeeManagement(menuData) {
      if (this.showEmployeeManagement) return menuData

      const config = FILTER_CONFIG.employeeManagement

      return menuData
        .filter((item) => item.text !== '员工管理')
        .map((item) => {
          if (item.children && config[item.text]) {
            const excludeTexts = config[item.text]
            return {
              ...item,
              children: item.children.filter((s) => !excludeTexts.includes(s.text)),
            }
          }
          return item
        })
    },

    /**
     * 套餐过滤
     */
    filterBySetMeal(menuData) {
      if (this.setMealCode === 80000) return menuData

      return menuData.map((item) => {
        if (item.text === '企业云展系统' && item.children) {
          return {
            ...item,
            children: item.children.filter((s) => s.text !== '我的企业动态'),
          }
        }
        return item
      })
    },

    /**
     * 身份过滤（个人用户）
     */
    filterByIdentity(menuData) {
      if (!this.isPerson) return menuData

      let result = menuData.filter((i) => i.text !== '客户管理系统')

      if (this.roleId === Role.Id.Designer) {
        result = result.filter((i) => i.text !== '我的采购')
      }

      return result
    },

    /**
     * 基础版过滤
     */
    filterByBaseVersion(menuData) {
      if (!this.isBaseUser) return menuData

      return menuData.map((item) => {
        if (item.text === '大数据获客' && item.children) {
          return {
            ...item,
            children: item.children.map((s) => {
              if (s.text === '外呼呼叫') {
                const { children, ...rest } = s
                return rest
              }
              return s
            }),
          }
        }
        return item
      })
    },

    /**
     * 语言环境过滤
     */
    filterByLocale(menuData) {
      if (!this.isEnglish) return menuData

      const config = FILTER_CONFIG.english
      let result = menuData

      // 访客角色特殊过滤
      if (this.roleId === 1) {
        result = result
          .filter((i) => !config.excludeMenus.includes(i.text))
          .map((item) => {
            if (item.children && config[item.text]) {
              return {
                ...item,
                children: item.children.filter((s) => !config[item.text].includes(s.text)),
              }
            }
            return item
          })
      }

      // 通用英文过滤
      return result.map((item) => {
        if (item.text === '获客工具' && item.children) {
          return {
            ...item,
            children: item.children.filter((s) => s.text !== '云招聘'),
          }
        }
        return item
      })
    },

    /**
     * 递归查找菜单路径
     */
    findMenuPath(list, targetName, targetPath, parents = []) {
      for (const item of list) {
        const isMatch = (targetName && item.key === targetName) || (targetPath && item.href === targetPath)

        if (isMatch) {
          return { key: item.key, parentKeys: parents }
        }

        if (this.hasChildren(item)) {
          const result = this.findMenuPath(item.children, targetName, targetPath, [...parents, item.key])
          if (result) return result
        }
      }
      return null
    },

    /**
     * 更新菜单选中和展开状态
     */
    updateMenuState(route) {
      if (!route) return

      const result = this.findMenuPath(this.menusOption, route.name, route.path)

      if (result) {
        this.selectedKeys = [result.key]

        if (!this.collapsed) {
          const mergedKeys = [...new Set([...this.openKeys, ...result.parentKeys])].map(String)
          this.openKeys = mergedKeys
        }
      }
    },

    /**
     * 菜单项点击处理
     */
    toMenuItem(item) {
      // 外部链接
      if (item.attr) {
        this.openExternalLink(item.attr)
        return
      }

      // 内部路由
      if (item.href) {
        if (this.$route.path === item.href) {
          // 点击当前页菜单：走 /memberRefresh 中转，刷新当前页
          this.$router.replace({
            path: '/memberRefresh',
            query: { path: this.$route.fullPath },
          })
        } else {
          this.$router.push({ path: item.href })
        }
        return
      }

      // 访客特殊处理
      this.handleVisitorAction(item)
    },

    /**
     * 打开外部链接
     */
    openExternalLink(url) {
      const newWindow = window.open()
      if (newWindow) {
        newWindow.opener = null
        newWindow.location = url
      }
    },

    /**
     * 访客特殊操作处理
     */
    handleVisitorAction(item) {
      if (this.$store.state.userInfo.roleId !== 1) return

      const { key } = item
      const { auditRoleId } = this.$store.state.userInfo

      if (key === 'visitor-myExhibit-exhibitSignup') {
        this.$store.commit('SWITCH_SHOW_CART', 'visitorExhibition')
      } else if (auditRoleId === 0) {
        this.$store.commit('SWITCH_SHOW_CART', 'certApplication')
      } else {
        this.$store.commit('SWITCH_SHOW_CART', 'applying')
      }
    },

    /**
     * 跳转首页
     */
    goHome(supply = false) {
      let homeUrl = process.env.RELEASE === 'stable' ? 'https://www.yzdli.com' : 'http://beta.yunhzh.com'
      if (supply) {
        homeUrl += '/supply'
      }
      window.location.href = homeUrl
    },

    /**
     * 跳转通知页
     */
    goNotifications() {
      this.$router.push(`${this.consolePath}/notifications`)
    },

    /**
     * 跳转企业主页
     */
    goHomePage() {
      if (this.roleId && this.roleId !== 1 && this.roleId !== 0) {
        this.$utils.toOuterLink(`/${this.memunicode}`, false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
/* 使用 scoped 并配合 ::v-deep (Vue2) 覆盖 Antd 样式
   如果你的环境不支持 ::v-deep，请尝试 /deep/ 或 >>>
*/

.clg-menu {
  position: relative;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: 1px 0px 0px 0px rgba(0, 0, 0, 0.04);

  .supplyChainBox {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
    min-height: 64px;
    box-sizing: border-box;
    transition: all 0.3s;
    cursor: pointer;

    .supplyLogo {
      width: 36px;
      height: 36px;
      border-radius: 6px;
      object-fit: cover;
      flex-shrink: 0;
    }

    .supplyTitle {
      margin-left: 10px;
      font-size: 15px;
      font-weight: 500;
      color: #333;
      word-break: break-word;
      max-width: 140px;
    }

    &.collapsed {
      justify-content: center;
      padding: 12px 8px;

      .supplyLogo {
        width: 32px;
        height: 32px;
      }
    }
  }

  .logoBox {
    position: relative;
    width: 126px;
    height: 40px;
    margin: 12px auto; // 居中优化
    background: url('https://oss-resource668.yzdli.com/website/image/consoleMenuLogo.png');
    background-size: 100% 100%;
    background-repeat: no-repeat;
    cursor: pointer;
  }

  .messageBox {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    bottom: 0;
    width: 100%;
    height: 40px;
    line-height: 40px;
    cursor: pointer;
    color: #333;
    font-size: 16px;
    background: #f9f9f9;
    z-index: 9; // 确保在最上层

    .ant-badge {
      margin-left: 5px;
    }
  }
}

#member-menu {
  font-size: 16px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 50px;
  border-right: none; // 去掉右侧边框

  // 隐藏滚动条但保留功能 (可选)
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }
}

/* 折叠状态样式 */
#member-menu.isCollapsed {
  ::v-deep .ant-menu-item,
  ::v-deep .ant-menu-submenu-title {
    padding: 0 8px !important; // 强制内边距
    text-align: center;

    .anticon {
      margin-right: 0 !important;
    }

    .menu-name,
    span {
      display: none; // 隐藏文字
    }
  }
}

/* 菜单项基础样式 */
.member-menu {
  ::v-deep .ant-menu-item,
  ::v-deep .ant-menu-submenu-title {
    font-weight: 400;
    font-size: 16px;
    color: #333;
    line-height: 48px;
    height: 48px;
    margin-top: 0;
    margin-bottom: 0;

    .anticon {
      margin-right: 8px;
    }
  }

  /* 选中样式去除右边框 */
  ::v-deep .ant-menu-item-selected::after {
    display: none !important;
  }

  .talkUnReadNum {
    background: #f23c3c;
    color: #fff;
    height: 16px; // 稍微调高一点
    line-height: 16px;
    position: absolute; // 绝对定位，避免挤压文字
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    text-align: center;
    border-radius: 8px;
    font-size: 12px;
    padding: 0 6px;
    min-width: 18px;
  }
}
</style>
